/**
 * VirtualTourEmbed — Weidner Via6 override.
 *
 * Mirrors the rise base component (iframe embed + per-tour selector
 * pulled from the `virtual_tours` collection) but swaps the outer
 * section background from white to the theme primary (deep Via6 navy)
 * so the embed sits on a branded surface that matches the rest of the
 * Via6 sub-pages (Header / InteriorBody / Footer all share this
 * primary surface).
 *
 * The tour-picker select also sits on the primary surface (text and
 * border switched to text-on-primary) so the selector blends with the
 * branded section instead of punching a white chip into it. The "no
 * tours available" fallback copy likewise uses text-on-primary so it
 * stays legible against the dark navy section background.
 */
import React, { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-[var(--vibe-color-primary)] pt-0 pb-8 md:pt-0 md:pb-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="flex justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <select
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            className="font-body text-sm bg-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-text-on-primary)] rounded px-3 py-2 focus:outline-none focus:border-[var(--vibe-color-secondary)] max-w-full truncate"
          >
            {validTours.map((tour, i) => (
              <option key={i} value={i}>
                {tour.virtual_tour_title || `Tour ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTour?.virtual_tour_url ? (
          <div className="relative w-full rounded-lg overflow-hidden border border-[var(--vibe-color-border)]" style={{ paddingBottom: '56.25%' }}>
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-on-primary)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
